import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Trash2, HelpCircle } from "lucide-react";
import CoachHeardPanel from "@/components/CoachHeardPanel";

const CATEGORY = { C01: "scope_creep", C05: "sunk_cost", C06: "complexity", C08: "scope_creep" };

export default function CoachLibrary({ projectId }) {
  const [data, setData] = useState(null);
  const [expl, setExpl] = useState({});
  const explain = async (id, deep) => {
    try {
      const { data } = await api.get("/coach/heard/explain", { params: { feature_id: id, category: CATEGORY[id] || "", deep } });
      setExpl(e => ({ ...e, [id]: deep ? (data.why_this_matters || data.why) : data.why }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/library", { params: { project_id: projectId } }); setData(data); } catch {}
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const setSetting = async (f, patch) => {
    try {
      await api.put("/coach/settings", { project_id: projectId, feature_id: f.id, ...patch });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const clearData = async () => {
    try { const { data } = await api.delete("/coach/data"); toast.success(data.note || "Coach data deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <p className="k-muted">Loading…</p>;
  return (
    <div data-testid="coach-library">
      <CoachHeardPanel projectId={projectId} />
      <div className="k-container" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BookOpen size={18} color="var(--accent)" />
          <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Coach library — {data.count} features</h3>
          <button className="k-btn k-btn-ghost" data-testid="delete-coach-data" style={{ marginLeft: "auto", padding: "6px 12px", fontSize: 12 }} onClick={clearData}>
            <Trash2 size={13} /> Delete my Coach data
          </button>
        </div>
        <ul className="k-muted" style={{ fontSize: 12, margin: "10px 0 0", paddingLeft: 18 }}>
          {data.global_rules.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 12 }}>
        {data.features.map(f => (
          <div key={f.id} className="k-container" data-testid={`coach-feature-${f.id}`} style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span className="k-heading" style={{ fontSize: 14, fontWeight: 600 }}>{f.id} · {f.name}</span>
              <label style={{ display: "flex", alignItems: "center" }}>
                <input type="checkbox" data-testid={`toggle-${f.id}`} checked={!!f.enabled}
                  onChange={e => setSetting(f, { enabled: e.target.checked })} />
              </label>
            </div>
            <div className="k-muted" style={{ fontSize: 12, marginTop: 6 }}><b>Trigger:</b> {f.trigger}</div>
            <div className="k-muted" style={{ fontSize: 12 }}><b>Action:</b> {f.action}</div>
            <div className="k-muted" style={{ fontSize: 12 }}><b>Safeguard:</b> {f.safeguard}</div>
            {f.core_mapping && <div className="k-tag-info" style={{ fontSize: 11, marginTop: 4 }}>{f.core_mapping}</div>}
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <button className="k-link" data-testid={`why-${f.id}`} style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => explain(f.id, false)}><HelpCircle size={11} /> Tell me why</button>
              <button className="k-link" data-testid={`why-deep-${f.id}`} style={{ fontSize: 11 }} onClick={() => explain(f.id, true)}>Why this matters</button>
            </div>
            {expl[f.id] && <div className="k-tag-info" data-testid={`expl-${f.id}`} style={{ fontSize: 11, marginTop: 6, padding: 6, borderRadius: 6 }}>{expl[f.id]}</div>}
            <div className="k-muted" style={{ fontSize: 11, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
              Frequency cap
              <input type="number" min="0" className="k-control" data-testid={`cap-${f.id}`} style={{ width: 64, padding: "4px 8px" }}
                value={f.frequency_cap} onChange={e => setSetting(f, { frequency_cap: parseInt(e.target.value || "0", 10) })} />
              <span className="k-badge" style={{ marginLeft: "auto" }}>{f.metric}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
